// On-demand, ROLE-SCOPED loader for the FAQ content translations.
//
// THE TWO PROBLEMS IT SOLVES
// --------------------------
// 1. WEIGHT. `registerStaticTranslations.js` is imported by `i18n.js`, which
//    every page imports. Every bundle listed there therefore ships in the MAIN
//    chunk. The FAQ tier bundles are ~3.9 MB of raw JSON (1471 rows × 16 locale
//    columns) — weight that every user downloaded on every page, for a page most
//    of them never open.
// 2. CONFIDENTIALITY (2026-08-10). A translated question text IS the question.
//    Shipping all tier bundles to everyone would have handed an agent the club
//    tiers in sixteen languages, no matter what the UI renders. Marin: "der
//    Agent und Agency darf sicher nicht wissen was alles die Clubs haben!"
//
// HOW IT WORKS
// ------------
// Each tier bundle sits behind its own `import()`, so webpack emits one async
// chunk per bundle. `loadFaqTranslations(branches)` fetches ONLY the chunks of
// the modules the viewer's branches own (the same table the dataset loader uses,
// constants/faq/faqBranchModules.js), hands them to the SAME registration
// routine the eager path uses (`registerBundles`), and memoises per module so
// repeated mounts of /faq (or a language switch) never fetch twice.
//
// WHY LATE REGISTRATION IS SAFE HERE
// ----------------------------------
// The `static` namespace is flat and global: the bundle registered LAST owns a
// shared English sentence for the whole app. These bundles share no English
// source sentence with any non-FAQ bundle — the two that did ("Clear",
// "Requests") live in faq-chrome.json, which stays statically registered. So the
// merged namespace ends up byte-identical to the eager order, and no other
// screen's wording can flip when a user visits /faq.
import i18n from '../i18n';
import { registerBundles } from './registerStaticTranslations';
import { modulesForBranches } from '../constants/faq/faqBranchModules';

// One entry per module of FAQ_MODULE_BRANCHES — same names, same split, so the
// question and its translation can never be scoped differently.
const BUNDLE_CHUNKS = {
  basis: () => import(/* webpackChunkName: "faq-tr-basis" */ './pages/faq/faq-basis.json'),
  agent: () => import(/* webpackChunkName: "faq-tr-agent" */ './pages/faq/faq-agent.json'),
  agentClub: () =>
    import(/* webpackChunkName: "faq-tr-agent-club" */ './pages/faq/faq-agent-club.json'),
  agentClubStaff: () =>
    import(
      /* webpackChunkName: "faq-tr-agent-club-staff" */ './pages/faq/faq-agent-club-staff.json'
    ),
  agentAgencyAdmin: () =>
    import(
      /* webpackChunkName: "faq-tr-agent-agencyadmin" */ './pages/faq/faq-agent-agencyadmin.json'
    ),
  agencyMember: () =>
    import(/* webpackChunkName: "faq-tr-agencymember" */ './pages/faq/faq-agencymember.json'),
  agencyAdmin: () =>
    import(/* webpackChunkName: "faq-tr-agencyadmin" */ './pages/faq/faq-agencyadmin.json'),
  staffFree: () =>
    import(/* webpackChunkName: "faq-tr-stafffree" */ './pages/faq/faq-stafffree.json'),
  staffAgent: () =>
    import(/* webpackChunkName: "faq-tr-staff-agent" */ './pages/faq/faq-staff-agent.json'),
  clubMember: () =>
    import(/* webpackChunkName: "faq-tr-clubmember" */ './pages/faq/faq-clubmember.json'),
  clubAdmin: () =>
    import(/* webpackChunkName: "faq-tr-clubadmin" */ './pages/faq/faq-clubadmin.json'),
  lawyer: () => import(/* webpackChunkName: "faq-tr-lawyer" */ './pages/faq/faq-lawyer.json'),
  player: () => import(/* webpackChunkName: "faq-tr-player" */ './pages/faq/faq-player.json'),
  organizer: () =>
    import(/* webpackChunkName: "faq-tr-organizer" */ './pages/faq/faq-organizer.json'),
};

// module name -> promise of "this bundle is registered". Memoised so two
// components (or a re-render) share one request, and so registerBundles never
// re-adds the same template patterns.
const pending = new Map();
const registered = new Set();

const loadOne = (name, i18nInstance) => {
  if (!pending.has(name)) {
    pending.set(
      name,
      BUNDLE_CHUNKS[name]()
        .then((module) => {
          registerBundles(i18nInstance, [module.default]);
          registered.add(name);
          return true;
        })
        .catch((error) => {
          // Let a later mount retry instead of pinning the failure forever.
          pending.delete(name);
          console.error(`Failed to load the FAQ translations (${name}):`, error);
          return false;
        })
    );
  }
  return pending.get(name);
};

/**
 * True once every bundle this viewer is entitled to is in i18next. Lets a
 * component render translated content immediately on a second visit instead of
 * showing its loading state again.
 *
 * @param {Set<string>|Array<string>} branches the viewer's own audience branches
 */
export const faqTranslationsRegistered = (branches) =>
  modulesForBranches(branches).every((name) => registered.has(name));

/**
 * Fetch + register the FAQ content bundles of the viewer's OWN branches.
 * Idempotent per module: promises are memoised, so concurrent callers share one
 * network request.
 *
 * Resolves `true` when everything asked for is registered and `false` when a
 * chunk could not be fetched (offline, deploy in flight). It never rejects — a
 * caller that gates its render on this must not be able to hang on it; the page
 * then renders its English source text, which is what it did before this split
 * existed.
 *
 * @param {Set<string>|Array<string>} branches the viewer's own audience branches
 */
export const loadFaqTranslations = (branches, i18nInstance = i18n) => {
  const names = modulesForBranches(branches);
  return Promise.all(names.map((name) => loadOne(name, i18nInstance))).then((results) =>
    results.every(Boolean)
  );
};

// Test hook — lets Jest start from a cold registry.
export const __resetFaqTranslationCache = () => {
  pending.clear();
  registered.clear();
};

export default loadFaqTranslations;
